import type React from "react";
import { useState } from "react";
import {
  Avatar,
  AvatarFallback,
  Box,
  Button,
  Icon,
  Menu,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Text,
  useIsMobile,
} from "@salestrak/ui";
import { LogoutConfirmDialog } from "#app/components/auth/logout-confirm-dialog";
import { SalesTrakLogo } from "#app/components/brand/salestrak-logo";

function AccountMenu({ email, compact }: { email: string | null; compact: boolean }) {
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  if (!email) return null;
  return (
    <>
      <Menu>
        {compact ? (
          // A phone header has no room for two lines of account text beside
          // the back action, so the trigger is the account's mark: a 32px
          // monogram on a 40px hit target. The menu it opens already names the
          // email in full, so nothing is lost — only moved one press away.
          <MenuTrigger
            render={<Button variant="ghost" size="icon-xl" aria-label={`Account: ${email}`} />}
          >
            <Avatar>
              <AvatarFallback>{email.trim().charAt(0).toUpperCase()}</AvatarFallback>
            </Avatar>
          </MenuTrigger>
        ) : (
          <MenuTrigger
            render={
              // minWidth 0 lets the trigger shrink under width pressure so the
              // email's truncate can engage instead of pushing the row wider.
              // The column stays stretched (never align="end"): an end-aligned
              // item is content-sized, so the email would overflow the trigger
              // on a narrow viewport instead of truncating. The lines right-align
              // by text-align instead.
              <Box
                as="button"
                direction="column"
                gap={6}
                paddingX="base"
                paddingY="sm"
                rounded="lg"
                minWidth={0}
                maxWidth={320}
                hoverBackground
              />
            }
          >
            <Text size="xs" weight="medium" variant="muted" align="right">
              Logged in as
            </Text>
            <Text size="sm" weight="medium" align="right" truncate>
              {email}
            </Text>
          </MenuTrigger>
        )}
        <MenuPopup align="end">
          <MenuGroup>
            <MenuGroupLabel>Accounts</MenuGroupLabel>
            <MenuItem>
              <Box direction="row" align="center" justify="between" gap="sm" fill="width">
                <Text size="sm" truncate>
                  {email}
                </Text>
                <Icon name="check" size="md" />
              </Box>
            </MenuItem>
          </MenuGroup>
          <MenuSeparator />
          <MenuItem onClick={() => setLogoutConfirmOpen(true)}>Log out</MenuItem>
        </MenuPopup>
      </Menu>
      <LogoutConfirmDialog
        via="workspace_screen"
        onOpenChange={setLogoutConfirmOpen}
        open={logoutConfirmOpen}
      />
    </>
  );
}

type WorkspaceScreenLayoutProps = {
  email: string | null;
  /** When set, the top-left logo becomes a quiet back action. */
  onBack?: () => void;
  /** Label for the back action. */
  backLabel?: string;
  /**
   * The back action signs the viewer out (there is no screen to return to).
   * A phone header shows back as a bare chevron, and a chevron that logs
   * you out is a trap, so on a phone the header keeps the logo instead and
   * the account menu's Log out — which confirms — carries the exit.
   */
  backSignsOut?: boolean;
  /**
   * Where the column sits in the viewport. `center` (default) for a form whose
   * height is settled. `top` for a screen whose content changes height under
   * the reader's hands — a searchable list: re-centering on every keystroke
   * moves the field they are typing in, so the column anchors to the top and
   * grows downward only.
   */
  anchor?: "center" | "top";
  /**
   * A bar pinned under the scroller: the one action the screen must never
   * scroll out of reach. It sits outside the scroll area like the header
   * does, so rows fade out toward it (the scroller's own edge fade) instead
   * of meeting a hairline, and it clears the home indicator.
   */
  footer?: React.ReactNode;
  children: React.ReactNode;
};

/** The header row's fixed height, mirrored by the spacer below the content so
 * the content centers optically against the full viewport, not against the
 * shorter region the header leaves behind. */
const HEADER_HEIGHT = 88;

/** A phone's header is one row of 40px controls with 8px above and below. */
const PHONE_HEADER_HEIGHT = 56;

export function WorkspaceScreenLayout({
  email,
  onBack,
  backLabel = "Back to SalesTrak",
  backSignsOut = false,
  anchor = "center",
  footer,
  children,
}: WorkspaceScreenLayoutProps) {
  const isMobile = useIsMobile();
  const headerHeight = isMobile ? PHONE_HEADER_HEIGHT : HEADER_HEIGHT;
  const back = onBack && !(isMobile && backSignsOut) ? onBack : undefined;
  return (
    <Box fill="flex" direction="column">
      {/* In flow, not overlaid: an absolute header collides with the centered
          content on short viewports (the title climbs into the account block).
          As a row it consumes its own height, so overlap is impossible at any
          viewport size and an overflowing list scrolls below it. */}
      <Box
        direction="row"
        align="center"
        justify="between"
        gap="md"
        paddingX={isMobile ? "md" : "lg"}
        height={headerHeight}
        shrink={false}
      >
        {/* The leading slot never shrinks: the account trigger is the side
            that gives way (its email truncates) on a narrow viewport. */}
        {back ? (
          <Box shrink={false}>
            {isMobile ? (
              // The platform's own back grammar when the bar is narrow: the
              // chevron alone, its words kept for assistive tech.
              <Button variant="ghost" size="icon-xl" aria-label={backLabel} onClick={back}>
                <Icon name="chevron-left" size="lg" color="muted" />
              </Button>
            ) : (
              // Re-provided Text/Icon color: Button's own TextClassContext
              // paints full foreground, too loud for a corner escape hatch.
              <Button variant="ghost" onClick={back}>
                <Icon name="chevron-left" size="md" color="muted" />
                <Text variant="muted">{backLabel}</Text>
              </Button>
            )}
          </Box>
        ) : (
          <Box padding="xs" shrink={false}>
            <SalesTrakLogo />
          </Box>
        )}
        <AccountMenu email={email} compact={isMobile} />
      </Box>

      {/* scrollFade masks the scroll edges only while content overflows past
          them, so a long list fades out toward the header instead of cutting
          hard at the row boundary. */}
      <ScrollArea scrollFade fill>
        {/* minHeight 100% (never height): the column must GROW past the
            viewport when content overflows — a fixed height with
            justify=center pushes overflow above the scroll origin, where it
            is unreachable. */}
        <Box
          direction="column"
          minHeight="100%"
          align="center"
          justify={anchor === "top" ? "start" : "center"}
          paddingX={isMobile ? "md" : "xl"}
        >
          <Box fill="width" maxWidth={500}>
            {children}
          </Box>
          {/* The centered column mirrors the header below so it centers
              against the full viewport; a top-anchored column has nothing to
              mirror and keeps only a breathing gutter. */}
          {anchor === "top" ? (
            <Box paddingBottom="xl" shrink={false} />
          ) : (
            <Box height={headerHeight} shrink={false} />
          )}
        </Box>
      </ScrollArea>

      {footer ? (
        <Box
          shrink={false}
          paddingX="md"
          paddingTop="base"
          className="pb-[calc(env(safe-area-inset-bottom,0px)+--spacing(3))]"
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}
